
import i18n from '@/lib/i18n';

export type ValidationErrors = Record<string, string>;

export interface PasswordValidationResult {
  isValid: boolean;
  requirements: {
    minLength: boolean;
    hasUppercase: boolean;
    hasLowercase: boolean;
    hasNumber: boolean;
    hasSpecialChar: boolean;
  };
  message?: string;
  strength: 'weak' | 'fair' | 'good' | 'strong';
}

export const validatePasswordRealTime = (password: string): PasswordValidationResult => {
  const requirements = {
    minLength: password.length >= 8,
    hasUppercase: /[A-Z]/.test(password),
    hasLowercase: /[a-z]/.test(password),
    hasNumber: /\d/.test(password),
    hasSpecialChar: /[!@#$%^&*(),.?":{}|<>]/.test(password),
  };

  const validCount = Object.values(requirements).filter(Boolean).length;
  const isValid = validCount >= 4 && requirements.minLength; // At least 4 out of 5 requirements met + min length

  let strength: 'weak' | 'fair' | 'good' | 'strong' = 'weak';
  if (validCount >= 5) strength = 'strong';
  else if (validCount >= 4) strength = 'good';
  else if (validCount >= 3) strength = 'fair';

  let message = '';
  if (password.length > 0 && !isValid) {
    const missing = [];
    if (!requirements.minLength) missing.push(i18n.t('validation:password.minLength', { min: 8 }));
    if (!requirements.hasUppercase) missing.push(i18n.t('validation:password.uppercase'));
    if (!requirements.hasLowercase) missing.push(i18n.t('validation:password.lowercase'));
    if (!requirements.hasNumber) missing.push(i18n.t('validation:password.number'));
    if (!requirements.hasSpecialChar) missing.push(i18n.t('validation:password.specialChar'));
    
    message = missing.join(', ');
  }

  return {
    isValid,
    requirements,
    message,
    strength
  };
};

export const validateRegistrationForm = (
  email: string,
  username: string,
  firstName: string,
  lastName: string,
  password: string
): ValidationErrors => {
  const errors: ValidationErrors = {};
  
  // Email validation
  if (!email.trim()) {
    errors.email = i18n.t('validation:email.required');
  } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
    errors.email = i18n.t('validation:email.invalid');
  }
  
  // Username validation
  if (!username.trim()) {
    errors.username = i18n.t('validation:username.required');
  } else if (username.length < 3) {
    errors.username = i18n.t('validation:username.minLength', { min: 3 });
  }
  
  // Name validation
  if (!firstName.trim()) {
    errors.firstName = i18n.t('validation:firstName.required');
  }
  
  if (!lastName.trim()) {
    errors.lastName = i18n.t('validation:lastName.required');
  }
  
  // Enhanced password validation
  if (!password) {
    errors.password = i18n.t('validation:password.required');
  } else {
    const validation = validatePasswordRealTime(password);
    if (!validation.isValid) {
      errors.password = i18n.t('validation:password.requirements');
    }
  }
  
  return errors;
};
